import { FC, ReactElement } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import classNames from 'classnames';
import { cn } from 'helpers/utils/classnames';
import { imageAmplienceToUrl } from 'helpers/utils/amplience/imageAmplienceToUrl';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import { getPageType } from 'helpers/utils/cjTagDataHelpers';
import { gtmClickCta } from 'helpers/utils/gtm-events/gtmInteraction';
import { gtmHeroInteraction } from 'helpers/utils/gtm-events/gtmEccomerce';
import { layout } from 'static/constants';
import { AmplienceImage } from '../types';
import { HeroBannerCopyPlacement } from './enum';
import GTMWrapper from '../gtm-wrapper';
import { AmpPromotionEvent } from '../../../../types/gtm-events/Ecommerce';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';

export interface AmplienceHeroBannerProps {
  title: string;
  subtitle?: string;
  cta?: string;
  index?: number;
  ctaLink?: string;
  imageAltText: string;
  image: AmplienceImage;
  mobileImage?: AmplienceImage;
  copyPlacement: keyof typeof HeroBannerCopyPlacement;
  isOnCarouselView?: boolean;
  isFromCarousel?: boolean;
  gtmEvents: AmpPromotionEvent[];
}

const AmplienceHeroBanner: FC<AmplienceHeroBannerProps> = ({
  ctaLink,
  image,
  imageAltText,
  mobileImage,
  index,
  title,
  copyPlacement,
  cta,
  isFromCarousel,
  subtitle,
  isOnCarouselView = true,
  gtmEvents,
}) => {
  const path = usePathname();
  const pageType = getPageType({
    locale: 'en',
    path: path,
  });
  const hasCopyContent = title?.trim() || subtitle?.trim() || (cta?.trim() && ctaLink?.trim());
  const [isDesktop] = useMediaQuery(desktop);
  const _image = isDesktop ? image : mobileImage || image;

  const renderCopyContent = (): ReactElement => {
    if (!hasCopyContent) {
      return <></>;
    }
    return (
      <div
        className={cn(
          `absolute h-fit text-white max-lg:left-1/2 max-lg:top-1/2 max-lg:m-0 max-lg:w-max max-lg:max-w-[80%] max-lg:-translate-x-1/2 max-lg:-translate-y-1/2 ${classNames(HeroBannerCopyPlacement[copyPlacement ?? 'MIDDLE'])} flex flex-col items-center gap-3 rounded-[6px] bg-black/40 p-[36px] transition-all duration-500`,
          { 'opacity-0': !isOnCarouselView, 'mx-[48px]': copyPlacement !== 'MIDDLE' },
        )}
      >
        <div
          className={classNames('flex gap-3 text-center', {
            'flex-col-reverse': !cta,
            'flex-col': cta !== undefined,
          })}
        >
          {subtitle?.trim() && (
            <p className="block text-center text-sm font-normal leading-[17px] lg:text-lg lg:leading-[21px]">
              {subtitle}
            </p>
          )}
          {title?.trim() && <h2 className="text-[28px] font-semibold leading-[34px]">{title}</h2>}
        </div>
        {cta?.trim() && ctaLink?.trim() && (
          <Link
            className="cursor-pointer rounded-md bg-[rgb(60,60,60)] px-5 py-2 text-sm font-medium leading-[17px] hover:bg-black"
            onClick={() => {
              gtmClickCta({ name: cta, location: pageType });
              gtmHeroInteraction('click', title + (subtitle ? ` | ${subtitle}` : ''), index || 0);
            }}
            href={ctaLink}
            aria-hidden={!isOnCarouselView}
            style={{ pointerEvents: isOnCarouselView ? 'auto' : 'none' }}
            aria-label={cta}
          >
            {cta}
          </Link>
        )}
      </div>
    );
  };

  return (
    <div className={cn('relative w-full transition-all', { 'opacity-30': !isOnCarouselView })}>
      <GTMWrapper
        className={cn('relative size-full', isFromCarousel ? 'lg:h-[630px]' : 'h-[430px]')}
        gtmEvents={gtmEvents}
      >
        <>
          <Image
            fill
            loader={amplienceImageLoader}
            src={imageAmplienceToUrl(_image)}
            sizes={`(max-width: 1023px) 50vw, ${layout.pageMaxWidth}px`}
            loading={isOnCarouselView ? 'eager' : 'lazy'}
            alt={imageAltText || ''}
            priority={isOnCarouselView}
            fetchPriority="high"
            className="object-cover lg:rounded-md"
          />

          {renderCopyContent()}
        </>
      </GTMWrapper>
    </div>
  );
};

export default AmplienceHeroBanner;
